import React from "react";
import Modal from "../../../componentLibrary/Modal";
import Combobox, { ComboboxOptionI } from "../../../componentLibrary/Combobox";
import { ComponentEnum, ComponentI } from "../../../types";
import Button from "../../../componentLibrary/Button";
import { SelectedComponentType } from "../utils/selectedComponentUtils";
import {
  getNewComponentContent,
  transferContentVariables,
} from "../utils/addComponent";
import { getComponentGroup, COMPONENT_GROUPS } from "../utils/componentGroup";
import { renderContentCard } from "./DraggableComponentCard";

export type PasteWhere =
  | "Beginning"
  | "End"
  | "Below Selected Component"
  | "Inside";

export default function PasteComponentModal({
  isModalOpen,
  setIsModalOpen,
  pasteComponent,
  setPasteComponent,
  handlePasteComponent,
  selectedComponent,
}: {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pasteComponent: ComponentI;
  setPasteComponent: React.Dispatch<React.SetStateAction<ComponentI>>;
  handlePasteComponent: (
    component: ComponentI,
    pasteWhere?: PasteWhere,
  ) => void;
  selectedComponent: SelectedComponentType;
}) {
  const componentRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>(
    {},
  );

  const handleContentChange = (
    componentId: string,
    field: string,
    value: string | undefined,
  ) => {
    if (componentId === pasteComponent._id) {
      setPasteComponent((prevComponent) => {
        if (!prevComponent) return prevComponent;

        const updatedContent = { ...prevComponent.content };

        if (value === undefined) {
          delete updatedContent[field];
        } else {
          updatedContent[field] = value;
        }
        return { ...prevComponent, content: updatedContent };
      });
    }
  };

  const [pasteWhere, setPasteWhere] = React.useState<PasteWhere>("Beginning");

  const [isMinimized, setIsMinimized] = React.useState(false);

  React.useEffect(() => {
    if (selectedComponent.component) {
      setPasteWhere("Below Selected Component");
    } else {
      setPasteWhere("Beginning");
    }
  }, [selectedComponent]);

  const options = [
    "Beginning",
    "End",
    ...(selectedComponent.component ? ["Below Selected Component"] : []),
    ...(selectedComponent.component?.type === ComponentEnum.LOGIC_COMPONENT ||
    selectedComponent.component?.type === ComponentEnum.FOR_EACH ||
    selectedComponent.component?.type === ComponentEnum.TOGGLE
      ? ["Inside"]
      : []),
  ];

  const handleTypeChange = (componentId: string, value: ComponentEnum) => {
    if (componentId === pasteComponent._id) {
      setPasteComponent((prevComponent) => {
        if (!prevComponent) return prevComponent;

        const newContent = getNewComponentContent(value);
        const transferredContent = transferContentVariables(
          prevComponent.content,
          newContent,
          prevComponent.type,
          value,
          COMPONENT_GROUPS,
        );

        return {
          ...prevComponent,
          type: value,
          content: transferredContent,
        };
      });
    }
  };

  return (
    <Modal
      title="Preview Component"
      size="xl"
      height="90vh"
      isOpen={isModalOpen}
      onCloseModal={() => {
        setIsModalOpen(false);
      }}
      footer={
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="secondary"
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handlePasteComponent(pasteComponent, pasteWhere);
              setIsModalOpen(false);
            }}
          >
            Confirm
          </Button>
        </div>
      }
    >
      <div className="space-y-2 mb-2">
        <Combobox
          label="Insert Component At"
          options={options.map((option) => ({
            label: option,
            value: option,
          }))}
          value={{ label: pasteWhere, value: pasteWhere } as ComboboxOptionI}
          onChange={(option) => {
            setPasteWhere(option.value as PasteWhere);
          }}
        />
      </div>
      {pasteComponent && (
        <>
          {renderContentCard(
            pasteComponent,
            handleContentChange,
            () => {},
            {},
            {
              component: pasteComponent,
              parentComponent: null,
            },
            () => {},
            [],
            componentRefs,
            handleTypeChange,
            isMinimized,
            setIsMinimized,
            () => {},
            getComponentGroup(pasteComponent.type),
          )}
        </>
      )}
    </Modal>
  );
}
